import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch, API, useAuth } from "@/App";
import { useSocketIo } from "@/hooks/useSocketIo";
import { Truck, Search, ExternalLink, Wifi, WifiOff, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUS_STEPS = ["PENDING", "WAITING", "LOADING", "READY", "DISPATCHED"];

const statusColors = {
  PENDING: "bg-slate-500",
  WAITING: "bg-amber-500",
  LOADING: "bg-blue-500",
  READY: "bg-cyan-500",
  DISPATCHED: "bg-emerald-500",
  CANCELLED: "bg-red-500",
};

const statusBadgeColors = {
  PENDING: "bg-slate-600/20 text-slate-400 border-slate-600/30",
  WAITING: "bg-amber-600/20 text-amber-400 border-amber-600/30",
  LOADING: "bg-blue-600/20 text-blue-400 border-blue-600/30",
  READY: "bg-cyan-600/20 text-cyan-400 border-cyan-600/30",
  DISPATCHED: "bg-emerald-600/20 text-emerald-400 border-emerald-600/30",
  CANCELLED: "bg-red-600/20 text-red-400 border-red-600/30",
};

const DispatchStatusBar = ({ status }) => {
  const idx = STATUS_STEPS.indexOf(status);
  if (status === "CANCELLED") return (
    <div className="flex items-center gap-1 mt-2">
      <div className="h-2 w-full rounded-full bg-red-500/50" />
      <span className="text-[9px] text-red-400 whitespace-nowrap">CANCELLED</span>
    </div>
  );
  return (
    <div className="flex items-center gap-0.5 mt-3">
      {STATUS_STEPS.map((s, i) => (
        <div key={s} className="flex-1 flex flex-col items-center">
          <div className="relative w-full">
            <div className={`h-2 w-full rounded-full transition-all duration-500 ${i <= idx ? statusColors[s] : "bg-slate-700/50"}`} />
            {i === idx && i < STATUS_STEPS.length - 1 && (
              <div className={`absolute top-0 right-0 h-2 w-3 rounded-full ${statusColors[s]} animate-pulse`} />
            )}
          </div>
          <span className={`text-[9px] mt-1 font-medium ${i <= idx ? "text-slate-300" : "text-slate-600"}`}>{s}</span>
        </div>
      ))}
    </div>
  );
};

export default function DispatchQueuePage() {
  const { user } = useAuth();
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [editDispatch, setEditDispatch] = useState(null);
  const [editForm, setEditForm] = useState({ vehicle_number: "", driver_name: "", driver_phone: "", loading_bay: "", status: "" });
  const [wsConnected, setWsConnected] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const wsRef = useRef(null);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const loadDispatches = useCallback(async ({ silent = false } = {}) => {
    try {
      const params = filterStatus !== "ALL" ? `?status=${filterStatus}` : "";
      const res = await authFetch(`${API}/factory/dispatch-queue${params}`);
      const json = await res.json();
      if (json.success) {
        setDispatches(json.data);
        setLastUpdatedAt(new Date());
      }
    } catch (e) {
      if (!silent) toast.error("Failed to load dispatch queue");
      console.error(e);
    }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { loadDispatches(); }, [loadDispatches]);

  // Auto-refresh: tab visibility + focus
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") loadDispatches({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [loadDispatches]);

  // Auto-refresh: silent poll every 30s
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") loadDispatches({ silent: true });
    }, 30000);
    return () => clearInterval(id);
  }, [loadDispatches]);

  const manualRefresh = async () => {
    setRefreshing(true);
    try { await loadDispatches({ silent: false }); toast.success("Dispatch queue refreshed"); }
    finally { setRefreshing(false); }
  };

  // Real-time updates — Socket.IO (migrated from /ws/factory raw WS, P1 cleanup iter 75).
  // Backend mirrors `notify_factory(type, data)` to Socket.IO `factory_event` in
  // rooms role_FACTORY + role_SUPER_ADMIN.
  useSocketIo({
    factory_event: (msg) => {
      const t = msg?.type;
      if (t === "NEW_DISPATCH" || t === "DISPATCH_UPDATED") {
        loadDispatches();
        if (t === "NEW_DISPATCH") {
          toast.info("New dispatch order received!", { duration: 3000 });
        }
        setWsConnected(true);
      }
    },
  });
  // Show "connected" once the singleton is up.
  useEffect(() => {
    const t = setTimeout(() => setWsConnected(true), 500);
    return () => clearTimeout(t);
  }, []);

  // Fallback polling every 30s
  useEffect(() => {
    const interval = setInterval(loadDispatches, 30000);
    return () => clearInterval(interval);
  }, [loadDispatches]);

  const openEdit = (d) => {
    setEditDispatch(d);
    setEditForm({ vehicle_number: d.vehicle_number || "", driver_name: d.driver_name || "", driver_phone: d.driver_phone || "", loading_bay: d.loading_bay || "", status: d.status });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/factory/dispatch-queue/${editDispatch.dispatch_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Dispatch ${editDispatch.dispatch_id} updated`);
        setEditDispatch(null);
        loadDispatches();
      }
    } catch (e) { toast.error("Failed to update dispatch"); }
  };

  const advanceStatus = async (dispatch_id, currentStatus) => {
    const idx = STATUS_STEPS.indexOf(currentStatus);
    if (idx < 0 || idx >= STATUS_STEPS.length - 1) return;
    const newStatus = STATUS_STEPS[idx + 1];
    try {
      const res = await authFetch(`${API}/factory/dispatch-queue/${dispatch_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Status: ${currentStatus} -> ${newStatus}`);
        loadDispatches();
      }
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleOrderClick = (orderId) => {
    if (isSuperAdmin) {
      window.open(`/orders?highlight=${orderId}`, "_blank");
    } else {
      // Factory admin - open order detail in same panel
      window.open(`/factory/dispatch?order=${orderId}`, "_self");
    }
  };

  const filtered = dispatches.filter((d) => {
    const s = search.toLowerCase();
    return (d.dealer_name || "").toLowerCase().includes(s) ||
      (d.order_id || "").toLowerCase().includes(s) ||
      (d.product_summary || "").toLowerCase().includes(s);
  });

  const pendingCount = dispatches.filter((d) => d.status !== "DISPATCHED" && d.status !== "CANCELLED").length;

  const statusCounts = dispatches.reduce((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="dispatch-queue-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Sora']">Dispatch Queue</h1>
          <p className="text-slate-400 text-sm mt-1 flex items-center gap-2">
            {pendingCount} orders pending dispatch
            {lastUpdatedAt && (
              <span className="text-[10px] text-slate-500">
                · Last updated {lastUpdatedAt.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {wsConnected ? (
            <Badge className="bg-emerald-900/30 text-emerald-400 text-[10px] border border-emerald-600/30 flex items-center gap-1" data-testid="ws-connected">
              <Wifi className="w-3 h-3" /> Live
            </Badge>
          ) : (
            <Badge className="bg-slate-700/30 text-slate-400 text-[10px] border border-slate-600/30 flex items-center gap-1" data-testid="ws-disconnected">
              <WifiOff className="w-3 h-3" /> Offline
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={manualRefresh}
            disabled={refreshing}
            className="h-8 bg-[#1C2B2B] border-emerald-900/30 text-slate-300 hover:bg-emerald-900/20 hover:text-white"
            data-testid="dispatch-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Status Summary Chips */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input type="text" placeholder="Search by dealer, order, product..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-600" data-testid="dispatch-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {["ALL", ...STATUS_STEPS, "CANCELLED"].map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${filterStatus === s ? "bg-emerald-600 text-white" : "bg-[#1C2B2B] text-slate-400 hover:text-white border border-emerald-900/30"}`}
              data-testid={`dispatch-filter-${s.toLowerCase()}`}>
              {s}
              {s !== "ALL" && statusCounts[s] > 0 && <span className="bg-white/10 px-1.5 py-0 rounded text-[10px]">{statusCounts[s]}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Dispatch Cards */}
      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-center text-slate-500 py-8">No dispatches found</p>}
        {filtered.map((d) => (
          <div key={d.dispatch_id} className={`bg-[#1C2B2B] border rounded-xl p-4 transition-all hover:border-emerald-700/50 ${d.priority === "URGENT" ? "border-red-600/40" : "border-emerald-900/30"}`} data-testid={`dispatch-card-${d.dispatch_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-semibold truncate">{d.dealer_name || d.party_id || "N/A"}</h3>
                  {d.priority === "URGENT" && <span className="px-1.5 py-0.5 rounded bg-red-600/20 text-red-400 text-[10px] font-bold shrink-0">URGENT</span>}
                  {d.dealer_location && <span className="text-[10px] text-slate-500">{d.dealer_location}</span>}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <button onClick={() => handleOrderClick(d.order_id)} className="text-emerald-400 text-xs hover:underline flex items-center gap-1" data-testid={`order-link-${d.dispatch_id}`}>
                    <ExternalLink className="w-3 h-3" /> {d.order_id}
                  </button>
                  <span className="text-slate-500 text-xs">{d.product_summary}</span>
                </div>
              </div>
              <Badge className={`border text-xs ml-2 ${statusBadgeColors[d.status] || ""}`} data-testid={`status-badge-${d.dispatch_id}`}>{d.status}</Badge>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-1">
              <div><p className="text-slate-500 text-[10px] uppercase">Quantity</p><p className="text-white text-sm font-mono">{d.total_bags} Bags ({d.total_mt} MT)</p></div>
              <div><p className="text-slate-500 text-[10px] uppercase">Vehicle</p><p className="text-white text-sm">{d.vehicle_number || <span className="text-slate-600">Not assigned</span>}</p></div>
              <div><p className="text-slate-500 text-[10px] uppercase">Driver</p><p className="text-white text-sm">{d.driver_name || <span className="text-slate-600">Not assigned</span>}</p></div>
              <div><p className="text-slate-500 text-[10px] uppercase">Loading Bay</p><p className="text-white text-sm">{d.loading_bay || <span className="text-slate-600">-</span>}</p></div>
              <div><p className="text-slate-500 text-[10px] uppercase">Created</p><p className="text-white text-sm">{d.created_at ? new Date(d.created_at).toLocaleDateString("en-IN", {day: "numeric", month: "short"}) : "-"}</p></div>
            </div>

            {/* Status Progress Bar */}
            <DispatchStatusBar status={d.status} />
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-emerald-900/20">
              {d.status !== "DISPATCHED" && d.status !== "CANCELLED" && (
                <button onClick={() => advanceStatus(d.dispatch_id, d.status)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusColors[STATUS_STEPS[STATUS_STEPS.indexOf(d.status) + 1] || ""] || "bg-emerald-600"} text-white hover:opacity-80`}
                  data-testid={`advance-status-${d.dispatch_id}`}>
                  {d.status === "PENDING" ? "Start Waiting" : d.status === "WAITING" ? "Start Loading" : d.status === "LOADING" ? "Mark Ready" : "Dispatch Now"}
                </button>
              )}
              {d.status !== "DISPATCHED" && d.status !== "CANCELLED" && (
                <button onClick={() => openEdit(d)} className="px-3 py-1.5 bg-white/5 text-slate-300 rounded-lg text-xs hover:bg-white/10 transition-colors" data-testid={`edit-dispatch-${d.dispatch_id}`}>Edit Details</button>
              )}
              <button
                onClick={() => handleOrderClick(d.order_id)}
                className="px-3 py-1.5 bg-emerald-600/10 text-emerald-300 rounded-lg text-xs hover:bg-emerald-600/20 transition-colors flex items-center gap-1"
                data-testid={`view-timeline-${d.dispatch_id}`}
              >
                <ExternalLink className="w-3 h-3" /> Order &amp; Timeline
              </button>
              {d.status === "DISPATCHED" && (
                <span className="text-emerald-400 text-xs font-medium flex items-center gap-1">
                  <Truck className="w-3.5 h-3.5" /> Dispatched
                  {d.updated_at && <span className="text-slate-500 ml-1">{new Date(d.updated_at).toLocaleString("en-IN", {dateStyle: "short", timeStyle: "short"})}</span>}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editDispatch} onOpenChange={(o) => !o && setEditDispatch(null)}>
        <DialogContent className="bg-[#1A2332] border-emerald-900/50 text-white">
          <DialogHeader><DialogTitle className="text-white font-['Sora']">Edit Dispatch {editDispatch?.dispatch_id}</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label className="text-xs text-emerald-600 font-medium">Vehicle Number</label>
              <input value={editForm.vehicle_number} onChange={(e) => setEditForm({ ...editForm, vehicle_number: e.target.value })} placeholder="GJ-01-AB-1234" className="w-full mt-1 px-3 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-600" data-testid="edit-vehicle" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Driver Name</label>
                <input value={editForm.driver_name} onChange={(e) => setEditForm({ ...editForm, driver_name: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-600" data-testid="edit-driver-name" />
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Driver Phone</label>
                <input value={editForm.driver_phone} onChange={(e) => setEditForm({ ...editForm, driver_phone: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-600" data-testid="edit-driver-phone" />
              </div>
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Loading Bay</label>
              <input value={editForm.loading_bay} onChange={(e) => setEditForm({ ...editForm, loading_bay: e.target.value })} placeholder="Bay A1" className="w-full mt-1 px-3 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-600" data-testid="edit-loading-bay" />
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Status</label>
              <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-600" data-testid="edit-status">
                {[...STATUS_STEPS, "CANCELLED"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <button type="submit" className="w-full py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors" data-testid="submit-dispatch-edit">Update Dispatch</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
